import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { mcDropDown, mcSave, mcCheck } from '@mc/react-ui-icons';
// @ts-ignore
import { v4 as uuidv4 } from 'uuid';
import { isEqual } from 'lodash-es';
import { Icon } from '../icon/Icon';
import { IData } from '../virtual-data/VirtualData';
import { FinalTableProps, FinalTableViewType } from './FinalTableProps';
import classNames from 'classnames';
import { ITitleProps, Title } from '../typography/Title';
import { Button } from '../button/Button';
import { Menu } from '../menu/Menu';
import { MenuItem } from '../menu/MenuItem';
import { MenuDivider } from '../menu/MenuDivider';
import { useTableProfiles, useTableViews } from './TableSettings';
import * as Classes from '../common/classes';
import { SaveByNameModalForm } from '../filter/SaveByNameModalForm';
import { RenameByNameModalForm } from '../filter/RenameByNameModalForm';
import { TableSettingsEnumType } from '../filter/types';
import {
  showConfirm,
  showDeleteConfirm,
  showInfo,
} from '../modal/modalFunctions';
import { filtersComputeQueryFilter } from '../filter/Filters';
import { Modal } from '../modal/Modal';

export type Props<DATA extends IData<ID>, FILTER, ID> = {
  title?: string;
  titleProps?: Omit<ITitleProps, 'children'>;
  finalTableProps: FinalTableProps<DATA, FILTER, ID>;
};

export const FinalTableTitle = <
  DATA extends IData<ID>,
  FILTER extends unknown,
  ID extends unknown
>({
  title,
  titleProps,
  finalTableProps,
}: Props<DATA, FILTER, ID>) => {
  const [profiles, setProfiles] = useTableProfiles(
    finalTableProps.config.settingsKey
  );
  const [views, setViews] = useTableViews(finalTableProps.config.settingsKey);
  const { settingsKey, disablePohled } = finalTableProps.config;

  const onView = (view?: FinalTableViewType) => {
    if (!view) {
      finalTableProps.setProfile(undefined);

      // @ts-ignore
      finalTableProps.setFilter({}, {});

      setViews({
        views: views.views,
        activeView: undefined,
      });

      setProfiles({
        profiles: profiles.profiles,
        activeProfile: undefined,
      });
    } else {
      const viewProfile = profiles.profiles.find(
        (profile) => profile.id === view?.view.profile
      );

      finalTableProps.setProfile(viewProfile?.profile);

      const queryData = filtersComputeQueryFilter(
        finalTableProps.config.filters?.type ?? 'default',
        view.view.filter,
        finalTableProps.config.filters?.filtersDef ?? []
      );
      // @ts-ignore
      finalTableProps.setFilter(view.view.filter, queryData);

      setViews({
        views: views.views,
        activeView: view,
      });
      setProfiles({
        profiles: profiles.profiles,
        activeProfile: viewProfile,
      });
    }
  };

  const saveView = useCallback(() => {
    Modal.show(({ onClose }) => (
      <SaveByNameModalForm
        type={TableSettingsEnumType.POHLED}
        onClose={onClose}
        onSubmit={async (data) => {
          const duplicitView = views.views.find(
            (item) => item.nazev === data.nazev
          );
          if (duplicitView) {
            const allowSubmit = await showConfirm({
              title: 'Pohled s daným názvem již existuje',
              message: 'Přejete si jej nahradit?',
            });
            if (allowSubmit) {
              // updateProfile(duplicitView);
              onClose();
            }
          } else {
            const activeView: FinalTableViewType = {
              id: uuidv4(),
              nazev: data.nazev,
              view: {
                filter: finalTableProps.formFilter as Record<string, unknown>,
                profile: profiles.activeProfile?.id ?? null,
              },
            };
            setViews({
              views: [...views.views, activeView],
              activeView: activeView,
            });
            onClose();
          }
        }}
      />
    ));
  }, [views.views, setViews]);

  const updateView = useCallback(
    (view: FinalTableViewType) => {
      if (!differsFromExisting) return;
      setViews({
        // @ts-ignore
        views: views.views.map((item) =>
          item.id === view.id
            ? {
                ...item,
                view: {
                  profile: profiles.activeProfile?.id,
                  filter: finalTableProps.formFilter,
                },
              }
            : item
        ),
        activeView: {
          ...views.activeView,
          view: {
            profile: profiles.activeProfile?.id ?? null,
            // @ts-ignore
            filter: finalTableProps.formFilter ?? null,
          },
        },
      });
    },
    [views.views, setViews]
  );

  const renameView = useCallback(
    (view: FinalTableViewType) => {
      Modal.show(({ onClose }) => (
        <RenameByNameModalForm
          type={TableSettingsEnumType.POHLED}
          onClose={onClose}
          initialValues={{
            nazev: views.activeView?.nazev,
          }}
          onSubmit={async (data) => {
            const duplicitView = views.views.find(
              (item) =>
                item.nazev === data.nazev && item.id !== views.activeView?.id
            );
            if (duplicitView) {
              await showInfo({
                title: 'Pohled s daným názvem již existuje',
              });
              return;
            }
            onClose();
            setViews({
              views: views.views.map((item) =>
                item.id === view.id
                  ? {
                      ...item,
                      nazev: data.nazev,
                    }
                  : item
              ),
              activeView: views.activeView
                ? {
                    ...views.activeView,
                    nazev: data.nazev,
                  }
                : undefined,
            });
          }}
        />
      ));
    },
    [views.views, setViews]
  );

  const removeView = async (view: FinalTableViewType) => {
    const permitRemove = await showDeleteConfirm({
      message: 'Smazat pohled?',
    });
    if (permitRemove) {
      finalTableProps.setProfile(undefined);

      // @ts-ignore
      finalTableProps.setFilter({}, {});

      setViews({
        views: views.views.filter((item) => item.nazev !== view.nazev),
        activeView: undefined,
      });

      setProfiles({
        profiles: profiles.profiles,
        activeProfile: undefined,
      });
    }
  };

  const differsFromExisting = useMemo<boolean>(() => {
    if (!views.activeView) return false;
    return (
      views.activeView.view.profile !== profiles.activeProfile?.id ||
      !isEqual(views.activeView.view.filter, finalTableProps.formFilter)
    );
  }, [views.activeView, profiles.activeProfile, finalTableProps.formFilter]);

  return (
    <div className={Classes.FINAL_TABLE_WHEADER_TITLE}>
      <Title
        {...titleProps}
        className={classNames(
          titleProps?.className,
          Classes.FINAL_TABLE_WHEADER_TITLE_WRAPPER
        )}
      >
        <div className={Classes.FINAL_TABLE_WHEADER_TITLE_TEXT}>
          {views.activeView ? views.activeView.nazev : title}
          {differsFromExisting ? '*' : null}
        </div>
        {settingsKey && !disablePohled && (
          <div className={Classes.FINAL_TABLE_WHEADER_TITLE_ACTION}>
            <Button
              style={'link'}
              icon={<Icon icon={mcDropDown} />}
              menu={
                <Menu>
                  {views.views.length ? (
                    <React.Fragment>
                      {views.activeView ? (
                        <React.Fragment>
                          <MenuDivider title={views.activeView.nazev} />
                          <MenuItem
                            text={'Přejmenovat'}
                            onClick={() =>
                              views.activeView && renameView(views.activeView)
                            }
                          />
                          <MenuItem
                            text={'Aktualizovat'}
                            onClick={() =>
                              views.activeView && updateView(views.activeView)
                            }
                            disabled={!differsFromExisting}
                          />
                          <MenuItem
                            text={'Odstranit'}
                            intent={'danger'}
                            onClick={() =>
                              views.activeView && removeView(views.activeView)
                            }
                          />
                        </React.Fragment>
                      ) : null}
                      <MenuDivider title={'Všechny pohledy'} />
                      {views.activeView ? (
                        <MenuItem
                          text={`${title} (výchozí)`}
                          onClick={() => onView()}
                        />
                      ) : null}
                      {views.views.map((view) => (
                        <MenuItem
                          icon={
                            isEqual(views.activeView, view) && (
                              <Icon fixedWidth icon={mcCheck} />
                            )
                          }
                          text={view.nazev}
                          onClick={() => onView(view)}
                        />
                      ))}
                    </React.Fragment>
                  ) : null}
                  <MenuItem
                    text={'Uložit jako nový pohled…'}
                    icon={<Icon icon={mcSave} />}
                    onClick={() => {
                      saveView();
                    }}
                    disabled={!profiles.activeProfile}
                    title={
                        !profiles.activeProfile
                            ? 'Není vybraný žádný profil'
                            : undefined
                    }
                  />
                </Menu>
              }
            ></Button>
          </div>
        )}
      </Title>
    </div>
  );
};
